export interface RelationshipOption {
  value: string;
  emoji: string;
}

export const RELATIONSHIPS: RelationshipOption[] = [
  { value: 'Mom', emoji: '🌷' },
  { value: 'Dad', emoji: '⛳' },
  { value: 'Partner', emoji: '❤️' },
  { value: 'Sister', emoji: '🪩' },
  { value: 'Brother', emoji: '🏀' },
  { value: 'Daughter', emoji: '🌸' },
  { value: 'Son', emoji: '☀️' },
  { value: 'Grandma', emoji: '🫖' },
  { value: 'Grandpa', emoji: '☕' },
  { value: 'Granddaughter', emoji: '🎀' },
  { value: 'Grandson', emoji: '🧸' },
  { value: 'Friend', emoji: '⭐' },
  { value: 'Me!', emoji: '🙋' },
  { value: 'Other', emoji: '✨' },
];

/** Relationship → emoji, mirroring the recipient anchor on the real board
 *  header. Falls back to the neutral sparkle for unknown values.
 *
 *  Self-shopping gets a gender-matched raising-hand variant when the caller
 *  passes the answered gender — the unqualified 🙋 renders as a MAN on
 *  Google/Windows/Twitter emoji fonts, which read wrong for a female
 *  self-shopper (2026-07-14 user interview). */
export const getEmojiForRelationship = (
  relationship: string,
  gender?: Gender | null,
): string => {
  if (relationship === 'Me!') {
    if (gender === 'female') return '🙋‍♀️';
    if (gender === 'male') return '🙋‍♂️';
  }
  return RELATIONSHIPS.find((r) => r.value === relationship)?.emoji ?? '✨';
};

export interface AgeChip {
  value: number;
  label: string;
  /** Optional second line for the chip — kid chips use this for the
   *  age bracket so it sits on its own row and doesn't wrap. */
  sublabel?: string;
  emoji: string;
}

export const KID_AGE_CHIPS: AgeChip[] = [
  { value: 0.1, label: 'Newborn', sublabel: '0–3 months', emoji: '👶' },
  { value: 0.5, label: 'Baby', sublabel: '3–12 months', emoji: '🍼' },
  { value: 1.5, label: 'Toddler', sublabel: '1–2 years', emoji: '🖍️' },
  { value: 4, label: 'Preschool', sublabel: '3–5 years', emoji: '🎨' },
  { value: 8, label: 'Elementary School', sublabel: '6–10 years', emoji: '⚽' },
  { value: 12, label: 'Middle School', sublabel: '11–14 years', emoji: '🎒' },
  { value: 16, label: 'High School', sublabel: '14–18 years', emoji: '🎧' },
];

export const ADULT_AGE_CHIPS: AgeChip[] = [
  { value: 25, label: '20s', emoji: '🌱' },
  { value: 35, label: '30s', emoji: '🪴' },
  { value: 45, label: '40s', emoji: '🌿' },
  { value: 55, label: '50s', emoji: '🌻' },
  { value: 65, label: '60s', emoji: '🌳' },
  { value: 75, label: '70s+', emoji: '🌲' },
];

export const ALWAYS_ADULT_RELATIONSHIPS = ['Mom', 'Dad', 'Grandma', 'Grandpa', 'Partner', 'Me!'];

/** Days per month (1-indexed), Feb clamped to 29 so a leap-year Feb 29 pick
 *  never gets silently truncated by a non-leap validation pass elsewhere. */
export const DAYS_IN_MONTH: Record<number, number> = {
  1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30,
  7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31,
};

// Relationships that could be either an adult or a child — these get an
// extra step in the quiz that asks "adult or child?" before age. Sister
// and Brother are excluded by design (treated as always-adult; a kid
// sister would be picked as Daughter or via Other).
export const NEEDS_LIFESTAGE_RELATIONSHIPS = [
  'Son',
  'Daughter',
  'Granddaughter',
  'Grandson',
  'Friend',
  'Other',
];

export type LifeStage = 'adult' | 'child';

export type Gender = 'female' | 'male' | 'other';

/**
 * SEASONAL: Mother's Day / Father's Day occasion options are hidden out of
 * season (Kate, 2026-08-03 — it's August, the holidays are ~9 months out).
 * Flip this to `true` around next April to bring both back for the spring
 * gifting window; all the gendered-occasion machinery in `useQuizFlow.ts`
 * and `useSearchPillState.ts` stays intact behind it. Stored occasions on
 * existing boards are unaffected — this only gates the picker menus.
 */
export const SEASONAL_GENDERED_OCCASIONS_ENABLED = false;

export interface HolidayOccasionOption {
  value: string;
  label: string;
  emoji: string;
}

/**
 * THE-1334: Christmas / Hanukkah tiles, shared by the quiz (`useQuizFlow.ts`)
 * and the board's Occasion dropdown (`useSearchPillState.ts`) so the two
 * surfaces can't drift on label/emoji/spelling. Gated by
 * `isHolidayOccasionSeason`, not a flag — the window turns itself on and off
 * every year with no manual flip (unlike `SEASONAL_GENDERED_OCCASIONS_ENABLED`
 * above, which this is intentionally independent of).
 */
export const HOLIDAY_OCCASION_OPTIONS: HolidayOccasionOption[] = [
  { value: 'Christmas', label: 'Christmas', emoji: '🎄' },
  { value: 'Hanukkah', label: 'Hanukkah', emoji: '🕎' },
];

/**
 * True from Oct 15 00:00 through Dec 31 23:59, device-local date. False
 * Jan 1 through Oct 14. Call with a frozen/injected `today`, never compute
 * at module scope — the caller decides when "now" is read (see `today` in
 * `UseQuizFlowOptions` and `useSearchPillState`'s options).
 */
export const isHolidayOccasionSeason = (today: Date): boolean => {
  const month = today.getMonth(); // 0-based: Oct = 9, Nov = 10, Dec = 11
  if (month === 10 || month === 11) return true;
  if (month === 9) return today.getDate() >= 15;
  return false;
};

export const getGenderFromRelationship = (rel: string): Gender => {
  const female = ['Mom', 'Sister', 'Daughter', 'Grandma', 'Granddaughter'];
  const male = ['Dad', 'Brother', 'Son', 'Grandpa', 'Grandson'];
  if (female.includes(rel)) return 'female';
  if (male.includes(rel)) return 'male';
  return 'other';
};

/** Subject pronoun for headline copy. Defaults to "they" when gender
 *  is unknown so first-pass placeholders read naturally. */
export const getSubjectPronoun = (gender: Gender | null): 'she' | 'he' | 'they' => {
  if (gender === 'female') return 'she';
  if (gender === 'male') return 'he';
  return 'they';
};

/** Possessive pronoun for CTA copy ("Create her board" / "his board" /
 *  "their board"). Same fallback rule as the subject form. */
export const getPossessivePronoun = (gender: Gender | null): 'her' | 'his' | 'their' => {
  if (gender === 'female') return 'her';
  if (gender === 'male') return 'his';
  return 'their';
};

/** Pre-fill for the board-name step. For relationships people actually call
 *  by the label itself ("Mom", "Grandpa", "Me"), seed the field with that
 *  word so the user can keep it with one tap. Everyone else starts blank and
 *  types the recipient's name. Editable in every case. */
export const getPrepopulatedBoardName = (relationship: string): string => {
  switch (relationship) {
    case 'Mom':
    case 'Dad':
    case 'Grandma':
    case 'Grandpa':
      return relationship;
    case 'Me!':
      return 'Me';
    default:
      return '';
  }
};
